import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { EventosService } from '../../core/services/eventos.service';
import { Router } from '@angular/router';

interface BackupConfig {
  title: string;
  description: string;
  api: {
    generateBackup: string;
  };
  options: {
    allowGeneration: boolean;
    downloadAutomatically: boolean;
  };
  messages: {
    backupSuccess: string;
    backupError: string;
  };
}

interface SuperUserConfig {
  data: string;
}

@Component({
  selector: 'app-back-up',
  templateUrl: './back-up.component.html',
  styleUrls: ['./back-up.component.scss']
})
export class BackUpComponent implements OnInit,OnDestroy {

  private destroy$ = new Subject<void>();

  // Configuraciones JSON
  config: BackupConfig | null = null;
  private superUserConfig: SuperUserConfig | null = null;

  // Token
  superUserToken = '';
  tokenValidated = false;
  validatingToken = false;
  tokenError = '';

  // Backup
  generatingBackup = false;
  backupError = '';
  backupMessage = '';
  backupFileName = '';

  // Configuración
  configLoading = true;
  configError = '';

  constructor(
    private http: HttpClient,
    private _eventosService:EventosService,
    private router:Router
  ) {}

  ngOnInit(): void {
    // this.loadConfig();
    this.loadSuperUserConfig();
  }

  /**
   * Carga la configuración del backup.
   * Archivo: assets/config/backup.json
   */
  private loadConfig(): void {
    this.configLoading = true;

    this.http
      .get<BackupConfig>('assets/ukey.json')
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.configLoading = false;
        })
      )
      .subscribe({
        next: (config) => {
          this.config = config;
        },
        error: () => {
          this.configError =
            'No se pudo cargar la configuración del backup.';
        }
      });
  }

  /**
   * Carga el token desde el JSON.
   * Archivo: assets/config/superuser.json
   */
  private loadSuperUserConfig(): void {
    this.http
      .get<SuperUserConfig>(
        'assets/ukey.json'
      )
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (config) => {
          if (
            config &&
            typeof config.data === 'string' &&
            config.data.trim().length > 0
          ) {
            this.superUserConfig = config;
          } else {
            this.tokenError =
              'El archivo de autorización no contiene un token válido.';
          }
        },
        error: () => {
          this.tokenError =
            'No se pudo cargar el archivo de autorización.';
        }
      });
  }

  /**
   * Valida el token introducido.
   */
  validateSuperUserToken(): void {
    if (this.validatingToken) {
      return;
    }

    this.tokenError = '';
    this.backupError = '';
    this.backupMessage = '';
    this.backupFileName = '';

    if (!this.superUserToken.trim()) {
      this.tokenError =
        'Introduce el token de superusuario.';
      return;
    }

    if (!this.superUserConfig) {
      this.tokenError =
        'La configuración de autorización no está disponible.';
      return;
    }

    this.validatingToken = true;

    const enteredToken = this.superUserToken.trim();
    const configuredToken = this.superUserConfig.data;

    if (enteredToken === configuredToken) {
      this.tokenValidated = true;
      this.tokenError = '';
    } else {
      this.tokenValidated = false;
      this.tokenError =
        'El token de superusuario no es válido.';
    }

    // Limpiar el campo de entrada.
    this.superUserToken = '';
    this.validatingToken = false;
  }

  /**
   * Genera el backup mediante el backend.
   */
generateBackup(): void {
  if (this.generatingBackup) {
    return;
  }

  this.generatingBackup = true;
  this.backupError = '';
  this.backupMessage = '';
  this.backupFileName = '';

  this._eventosService.getEventosBack()
    .pipe(
      finalize(() => {
        this.generatingBackup = false;
      })
    )
    .subscribe({
      next: (resp: Blob) => {
        if (!resp || resp.size === 0) {
          this.backupError = 'El script SQL está vacío.';
          return;
        }

        const url = window.URL.createObjectURL(resp);
        const enlace = document.createElement('a');

        this.backupFileName = 'backup.sql.txt';

        enlace.href = url;
        enlace.download = this.backupFileName;

        document.body.appendChild(enlace);
        enlace.click();
        enlace.remove();

        window.URL.revokeObjectURL(url);

        this.backupMessage =
          'El script SQL de eventos se descargó correctamente.';

setTimeout(() => {
    this.router.navigateByUrl('account/login')
}, 5000);

      },error: (error) => {
        console.error('Error al generar el script:', error);

        this.backupError =
          'No fue posible descargar el script SQL de eventos.';
      }
    });
}

  /**
   * Cancela el proceso y limpia los estados locales.
   */
  cancelBackup(): void {
    if (
      this.generatingBackup ||
      this.validatingToken
    ) {
      return;
    }

    this.superUserToken = '';
    this.tokenValidated = false;

    this.tokenError = '';
    this.backupError = '';
    this.backupMessage = '';
    this.backupFileName = '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}